// This hook is responsible for fetching the repository objects (files and folders).
// There should be no other hook responsible for fetching repository objects. It will be used in the file explorer component and the table view component in the file explorer.
import { gql, useLazyQuery } from "@apollo/client";
import {
  GetRepositoryObjectsInput,
  RepositoryObject,
  RepositoryObjectFolder,
  RepositoryTree,
  Sort,
  Filter,
  Repo
} from "../../types";
import { useEffect, useState } from "react";
import { getTreeHierarchyFromRepositoryObjects } from "../../../helpers/treeHelper";

export const GET_REPOSITORY_OBJECTS = gql`
  query getRepositoryObjects(
    $repoId: ID!
    $pageNumber: Int
    $pageSize: Int
    $location: String
    $recursive: Boolean
    $foldersOnly: Boolean
    $versioned: Boolean
    $linked: Boolean
    $versionId: String
    $name: String
    $md5Sum: String
    $historicalInput: GetHistoricalRepositoryObjectsInput
    $sort: [Sort]
    $filter: Filter
  ) {
    getRepositoryObjects(
      input: {
        repoId: $repoId
        pageNumber: $pageNumber
        pageSize: $pageSize
        location: $location
        recursive: $recursive
        foldersOnly: $foldersOnly
        versioned: $versioned
        linked: $linked
        versionId: $versionId
        name: $name
        md5Sum: $md5Sum
        historicalInput: $historicalInput
        sort: $sort
        filter: $filter
      }
    ) {
      repositoryObjects {
        repository {
          id
          name
          isStandard
        }
        name
        versionId
        revision
        md5Sum
        size
        createdAt
        createdBy
        deletedAt
        comments {
          id
          text
          created
          createdBy
        }

        validity {
          qualityCheckStatus
          overallValidityStatus
          userValidityStatus
          systemValidityStatus
          outdatedUserValidityStatus
        }
      }
      historicDatetime
      totalCount
    }
  }
`;

export function useRepositoryObjects(
  mainRepositoryId: string,
  setTableView: boolean = true
) {
  const [
    callRepositoryObjects,
    {
      loading: isGetRepositoryObjectsLoading,
      error: getRepositoryObjectsError,
      refetch: refetchRepositoryObjects,
      variables: getRepositoryObjectsVariables
    }
  ] = useLazyQuery(GET_REPOSITORY_OBJECTS, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      onCompletedRepositoryObjects(data); // call the onCompleted function when the query is completed
    },

    pollInterval: 2 * 60 * 1000 // just to get the latest view every 2 minutes
  });
  const [filesFinal, setFilesFinal] = useState<RepositoryObject[]>([]);
  const [tableViewFiles, setTableViewFiles] = useState<RepositoryObject[]>([]);
  const [historicalDatetime, setHistoricalDatettime] = useState<string>("");
  const [foldersAndCount, setFoldersandCount] = useState<
    RepositoryObjectFolder[]
  >([]);

  const sortRepositoryObjects = (repositoryObjects: RepositoryObject[]) => {
    return repositoryObjects.sort((a, b) => {
      const aIsFolder = a.name.endsWith("/");
      const bIsFolder = b.name.endsWith("/");

      if (aIsFolder && !bIsFolder) {
        return -1;
      } else if (!aIsFolder && bIsFolder) {
        return 1;
      } else {
        return a.name.localeCompare(b.name); // sort alphabetically
      }
    });
  };

  const sortAndSetTableView = (repositoryObjects: RepositoryObject[]) => {
    const sortedRepositoryObjects = sortRepositoryObjects(repositoryObjects);
    setTableViewFiles(sortedRepositoryObjects);
  };

  const addFolderCountIfNotExists = (
    data: {
      getRepositoryObjects: {
        repositoryObjects: { repository: Repo }[];
        totalCount: number;
      };
    },
    location: string,
    pageNumber: number
  ) => {
    setFoldersandCount((prev) => {
      if (data.getRepositoryObjects.repositoryObjects.length === 0) {
        return prev;
      }
      const folderEntry = {
        repository: data.getRepositoryObjects.repositoryObjects[0].repository,
        folderName: location,
        count: data.getRepositoryObjects.totalCount,
        currentPageNumber: pageNumber
      };
      const index = prev.findIndex(
        (item: RepositoryObjectFolder) =>
          item.folderName === location &&
          item.repository.id === folderEntry.repository.id
      );
      if (index !== -1) {
        // Update currentPageNumber if exists
        const updated = [...prev];
        updated[index] = {
          ...updated[index],
          currentPageNumber:
            updated[index].currentPageNumber > pageNumber
              ? updated[index].currentPageNumber
              : pageNumber,
          count: data.getRepositoryObjects.totalCount
        };
        return updated;
      }
      return [...prev, folderEntry];
    });
  };

  // will call only once since activity data does not change
  // when browsing through files and folder
  const callMultipleRepository = async (
    repositoryObjectsInput: GetRepositoryObjectsInput[],
    setTableView: boolean = true
  ) => {
    if (!repositoryObjectsInput?.length) return;

    for (let i = 0; i < repositoryObjectsInput.length; i++) {
      const { data } = await callRepositoryObjects({
        variables: {
          ...repositoryObjectsInput[i]
        }
      });

      if (data?.getRepositoryObjects?.repositoryObjects) {
        if (
          repositoryObjectsInput[i].repoId === mainRepositoryId &&
          setTableView
        ) {
          sortAndSetTableView(data.getRepositoryObjects.repositoryObjects);
          setHistoricalDatettime(data.getRepositoryObjects.historicDatetime);
        }
        addFolderCountIfNotExists(data, "/", 1);
      }
    }
  };

  const getParentPath = (incoming: RepositoryObject[]): string | null => {
    let parentName = null;
    if (incoming && incoming.length > 0) {
      // Find the common parent path from all incoming names
      const splitPaths = incoming.map((obj) =>
        obj.name.split("/").filter(Boolean)
      );
      if (splitPaths.length > 0) {
        let commonParts = splitPaths[0];
        for (let i = 1; i < splitPaths.length; i++) {
          let j = 0;
          while (
            j < commonParts.length &&
            j < splitPaths[i].length &&
            commonParts[j] === splitPaths[i][j]
          ) {
            j++;
          }
          commonParts = commonParts.slice(0, j);
        }
        parentName = commonParts.length > 0 ? commonParts.join("/") : null;
      }
    }
    return parentName ? parentName + "/" : null;
  };

  const identifyPrevFilesAndSet = (
    repositoryObjects: RepositoryObject[],
    isAppend: boolean = false
  ) => {
    const incoming = repositoryObjects;
    const parentName = getParentPath(incoming);

    if (isAppend) {
      setFilesFinal((prevFiles) => {
        return Array.from(new Set([...prevFiles, ...incoming]));
      });
      return;
    }

    let filterPrevFiles = filesFinal.filter((file) => {
      if (parentName) {
        return (
          file.name.startsWith(parentName) &&
          file.repository.id === incoming[0].repository.id &&
          file.name !== parentName
        );
      }
    });

    let removeRepositoryObjectsList: RepositoryObject[] = [];
    filterPrevFiles.forEach((file: any) => {
      const exists = incoming.some(
        (incoming: RepositoryObject) => incoming.name === file.name
      );
      if (!exists) {
        removeRepositoryObjectsList.push(file);
      }
    });

    setFilesFinal((prevFiles) => {
      const filteredPrevFiles = prevFiles.filter(
        (file) =>
          !removeRepositoryObjectsList.some(
            (removeFile) => removeFile.name === file.name
          )
      );
      return Array.from(new Set([...filteredPrevFiles, ...incoming]));
    });
  };

  const onCompletedRepositoryObjects = (getRepositoryObjectsData: any) => {
    if (!getRepositoryObjectsData) return;
    const incoming =
      getRepositoryObjectsData.getRepositoryObjects.repositoryObjects;
    const isAppend =
      getRepositoryObjectsVariables?.pageNumber &&
      getRepositoryObjectsVariables.pageNumber > 1;
    identifyPrevFilesAndSet(incoming, !!isAppend);

    if (
      getRepositoryObjectsVariables?.sort ||
      getRepositoryObjectsVariables?.filter
    ) {
      setTableViewFiles(incoming);
    } else {
      setTableViewFiles(sortRepositoryObjects(incoming));
    }

    if (getRepositoryObjectsVariables?.location) {
      addFolderCountIfNotExists(
        getRepositoryObjectsData,
        getRepositoryObjectsVariables.location,
        getRepositoryObjectsVariables.pageNumber
          ? getRepositoryObjectsVariables.pageNumber
          : 1
      );
    }

    if (setTableView) {
      if (
        !getRepositoryObjectsVariables?.sort &&
        !getRepositoryObjectsVariables?.filter
      ) {
        sortAndSetTableView(
          getRepositoryObjectsData.getRepositoryObjects.repositoryObjects
        );
      } else {
        // use backend sorted and filtered data when requested in datagrid
        setTableViewFiles(
          getRepositoryObjectsData.getRepositoryObjects.repositoryObjects
        );
      }
      return;
    }
  };

  const refetchRepositoryObjectsWithTimeout = async (
    timeout: number = 3500,
    repositoryObjectsInput?: GetRepositoryObjectsInput
  ) => {
    return new Promise<void>((resolve) => {
      setTimeout(async () => {
        await refetchRepositoryObjects(repositoryObjectsInput);
        resolve();
      }, timeout);

      setTimeout(async () => {
        await refetchRepositoryObjects(repositoryObjectsInput);
      }, timeout * 4);
    });
  };

  // organized repository in their tree view
  const repositoryObjectsTree =
    getTreeHierarchyFromRepositoryObjects(filesFinal);
  const treeHierarchy: RepositoryTree[] = repositoryObjectsTree.sort(
    (a: RepositoryTree, b: RepositoryTree) => {
      // Put main repository first
      if (a.repository.id === mainRepositoryId) return -1;
      if (b.repository.id === mainRepositoryId) return 1;
      return 0;
    }
  );

  return {
    files: filesFinal, // flatten repository objects,
    foldersAndCount,
    tableViewFiles, // files to be shown in the table view on the right side when selecting a repository/folder in the tree view on the left side
    callMultipleRepository, // on component mount
    callRepositoryObjects,
    isGetRepositoryObjectsLoading,
    getRepositoryObjectsError,
    treeHierarchy,
    historicalDatetime,
    refetchRepositoryObjects: refetchRepositoryObjectsWithTimeout,
    sortAndSetTableView
  };
}
